const atomicRule = (key: string) => {
  return () => key;
};

const valueRule = <T = unknown>(key: string) => {
  return (v: T) => `${key}:${v}`;
};

// const optionalValueRule = <T = unknown>(key: string) => {
//   return (v?: T) => (isUndefined(v) ? key : `${key}:${v}`);
// };

const valueRuleWithDefault = <T = unknown>(key: string, defu: T) => {
  return (v?: T) => `${key}:${v || defu}`;
};

export const Rule = {
  required: atomicRule("required"),
  numeric: valueRule<number>("numeric"),
  confirmed: valueRule<string>("confirmed"),
  email: atomicRule("email"),
  password: atomicRule("max:24|lower|upper|digit|special|min:8"),
  // pricing: optionalValueRule<PricingModelLike>("pricing"),
  pricing: atomicRule("pricing"),
  address: atomicRule("address"),

  // presets
  shortTitle: valueRuleWithDefault<number>("max", 20),
  title: valueRuleWithDefault<number>("max", 72),
  longTitle: valueRuleWithDefault<number>("max", 144),
  short: valueRuleWithDefault<number>("max", 320),
  url: atomicRule("url"),

  group(rules: (string | undefined)[]) {
    return rules.filter((r) => !!r).join("|");
  },

  remote: function (rules: Record<string, string> | string[] | string) {
    const { loading, validate } = useRemoteValidate();
    type Ctx = {
      loading: typeof loading;
      _remote: true;
    };
    const fn = function (
      this: Ctx,
      value: unknown
    ): ReturnType<typeof validate> {
      return validate(value, rules);
    };
    fn.loading = loading;
    fn._remote = true;
    return fn;
  },

  get string() {
    return {
      min: valueRule<number>("min"),
      max: valueRule<number>("max"),
      lower: atomicRule("lower"),
      upper: atomicRule("upper"),
      digit: atomicRule("digit"),
      special: atomicRule("special"),
      size: (v: number) => `min:${v}|max:${v}`,
      regex: (pattern: RegExp, message?: string) =>
        message
          ? `regex:${pattern.source}:${message}`
          : `regex:${pattern.source}`,
    };
  },
  get number() {
    return {
      min: valueRule<number>("min_value"),
      max: valueRule<number>("max_value"),
      /**
       * Greater than zero
       */
      gtZero: atomicRule("greater_than_zero"),
      positive: atomicRule("positive_value"),
      between: (min: number, max: number) =>
        `min_value:${min}|max_value:${max}`,
      /**
       * Greater than zero and less than or equal to max
       * @param max
       */
      gtZeroLte: (max: number) => `greater_than_zero|max_value:${max}`,
    };
  },
  get date() {
    return {
      before: valueRule<string>("before"),
      after: valueRule<string>("after"),
    };
  },
  get array() {
    return {
      in: valueRule<unknown[]>("in"),
      notIn: valueRule<unknown[]>("notIn"),
      min: valueRule<number>("min_count"),
      max: valueRule<number>("max_count"),
    };
  },
};
